/* @flow */
import React from 'react';
import * as T from 'prop-types';
import classnames from 'classnames/bind';
import { IO } from 'trustar/fp';
import { prop } from 'ramda';
import { Observable, type Subscription } from 'rxjs';
import {Button, IconButton, IconButtonPure} from 'trustar-2.0/components/Button';
import {Icon, IconLookup} from 'trustar-2.0/components';
import s from './Carousel.styl';
const cx = classnames.bind(s);

const DEFAULT_SLIDE_WIDTH = 540;

const keydown$ = Observable.defer(() => Observable.fromEvent(document.body, 'keydown'));
const arrowLeft$ = keydown$.filter(e => e.key === 'ArrowLeft');
const arrowRight$ = keydown$.filter(e => e.key === 'ArrowRight');

class PrevButton extends React.Component {
  static contextTypes = {
    prev: T.func.isRequired,
  };

  // Warn users that onClick will be overwritten
  componentDidMount() {
    if (this.props.onClick) { // eslint-disable-line react/prop-types
      console.warn('PrevButton: You passed a value for `onClick` to the Carousel.PrevButton component. It will be ignored.');
    }
  }

  render() {
    return (
      <Button fill={'none'} {...this.props} onClick={this.context.prev} />
    );
  }
}

class NextButton extends React.Component {
  static contextTypes = {
    next: T.func.isRequired,
  };

  // Warn users that onClick will be overwritten
  componentDidMount() {
    if (this.props.onClick) { // eslint-disable-line react/prop-types
      console.warn('NextButton: You passed a value for `onClick` to the Carousel.NextButton component. It will be ignored.');
    }
  }

  render() {
    return (
      <Button fill={'none'} {...this.props} onClick={this.context.next} />
    );
  }
}

/**
 * NOTE: I didn't export Slide directly in order to emphasize it's relationship
 * to Carousel. They are both needed together and neither is useful without the
 * other, so the Slide is only exposed through the Carousel class.
 */
class Slide extends React.Component {
  static defaultProps = {
    style: {},
  };

  props: {
    children: React.Element<*>,
    width: number,
    style: Object,
  };

  render() {
    const { style, width } = this.props;
    return (
      <div style={{ ...style, width }} className={cx('Slide')}>
        {this.props.children}
      </div>
    );
  }
}

type CarouselProps = {
  children: React.Element<*>,
  withPaginator?: boolean,
  className?: string,
};

export class Carousel extends React.Component {
  static Slide = Slide; // See NOTE above Slide
  static PrevButton = PrevButton;
  static NextButton = NextButton;

  static defaultProps = {
    withPaginator: false,
  };

  static childContextTypes = {
    prev: T.func.isRequired,
    next: T.func.isRequired,
  };

  getChildContext() {
    return {
      prev: this.handlePrev,
      next: this.handleNext,
    };
  }

  props: CarouselProps;

  slideCount: number;

  slideWindow: Element;

  sub: Subscription;

  state = {
    currentIndex: 0,
    slideWidth: DEFAULT_SLIDE_WIDTH, // Just a default. DOM will be measured
  };

  constructor(props: CarouselProps) {
    super(props);

    if (process.env.NODE_ENV === 'development') {
      React.Children.forEach(props.children, child => {
        if (child.type.displayName !== 'Slide') {
          console.warn(`
            Carousel only accepts children of type Carousel.Slide.
            Check the constructor of Carousel.js. ಠ_ಠ
          `);
        }
      });
    }

    // Not storing this on state because it does not affect render
    this.slideCount = React.Children.count(props.children);
  }

  componentDidMount() {
    const slideWidth = IO(() => this.slideWindow)
      .map(x => x.getBoundingClientRect())
      .map(prop('width'));

    this.sub = Observable.merge(
      arrowLeft$.do(this.handlePrev),
      arrowRight$.do(this.handleNext),
    ).subscribe();

    this.setState({
      slideWidth: slideWidth.foldOr(() => this.state.slideWidth),
    });
  }

  componentWillUnmount() {
    this.sub && this.sub.unsubscribe();
  }

  handlePrev = () => {
    const hasPrev = this.state.currentIndex > 0;
    if (hasPrev) {
      this.setState({
        currentIndex: this.state.currentIndex - 1,
      });
    }
  };

  handleNext = () => {
    const hasNext = this.state.currentIndex < this.slideCount - 1;
    if (hasNext) {
      this.setState({
        currentIndex: this.state.currentIndex + 1,
      });
    }
  };

  jumpTo = (index: number) => () => {
    this.setState({ currentIndex: index });
  };

  /**
   * NOTE: The cloneElement that's going on here is only to pass the measured
   * width of the slides to the slide components themselves. Another way to do
   * this would be with context, but the react docs discourage using context in
   * general but EVEN MORE SO for values that change. Since there's no way to
   * guarantee a child will update in response to context updating. I.e. if
   * there is an intermediate PureComponent or any intermediate component
   * returns false from shouldComponentUpdate.
   */
  render() {
    const { children, className } = this.props;
    const { currentIndex } = this.state;
    const hasNext = currentIndex < this.slideCount - 1;
    return (
      <div className={cx('Carousel', className)}>

        {currentIndex > 0 ? (
          <IconButtonPure fill={'none'} large icon={IconLookup.CHEVRON_LEFT} strictStyle={false} onClick={this.handlePrev} className={cx('prev')} />
        ) : <div style={{ width: 40 }} />}
        <div ref={el => { this.slideWindow = el; }} className={cx('slideWindow')}>
          <div
            style={{
              width: this.state.slideWidth * this.slideCount,
              transform: `translateX(-${this.state.currentIndex * this.state.slideWidth}px)`,
            }}
            className={cx('slides')}>
            {/* See NOTE */}
            {React.Children.map(children, (child) => (
              React.cloneElement(child, { width: this.state.slideWidth })
            ))}
          </div>
        </div>
        {hasNext && (
          <IconButtonPure fill={'none'} large icon={IconLookup.CHEVRON_RIGHT} strictStyle={false} onClick={this.handleNext} className={cx('prev')} />
        )}

        {/* Paginator is optional */}
        {this.props.withPaginator && (
          <div className={cx('dots')}>
            {Array(this.slideCount).fill(null).map((_, i) => (
              <span
                key={i}
                className={cx('dot', {
                  active: this.state.currentIndex === i,
                })}
                onClick={this.jumpTo(i)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./web_modules/trustar/components/Carousel.js